<template>
  <Transition name="loader-fade">
    <div v-if="showLoader" class="global-loader-overlay">
      <!-- Background blur overlay -->
      <div class="loader-backdrop"></div>
      
      <!-- Main loader container -->
      <div class="loader-container">
        <!-- Animated logo/spinner -->
        <div class="loader-spinner-wrapper">
          <!-- Outer rotating ring -->
          <div class="spinner-ring outer-ring"></div>
          <!-- Middle pulsing ring -->
          <div class="spinner-ring middle-ring"></div>
          <!-- Inner rotating ring (reverse) -->
          <div class="spinner-ring inner-ring"></div>
          <!-- Center logo -->
          <div class="spinner-center">
            <svg class="center-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M2 17L12 22L22 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M2 12L12 17L22 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>

        <!-- Progress bar -->
        <div class="loader-progress-container">
          <div class="loader-progress-track">
            <div class="loader-progress-bar" :style="{ width: progress + '%' }">
              <div class="progress-glow"></div>
            </div>
          </div>
          <span class="progress-text">{{ Math.round(progress) }}%</span>
        </div>

        <!-- Loading text with typing animation -->
        <div class="loader-text-container">
          <span class="loader-text">{{ loadingText }}</span>
          <span class="loader-dots">
            <span class="dot"></span>
            <span class="dot"></span>
            <span class="dot"></span>
          </span>
        </div>

        <!-- Floating particles -->
        <div class="particles">
          <div v-for="i in 12" :key="i" class="particle" :style="getParticleStyle(i)"></div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';
import { useLoaderStore } from '@/store/loader';

export default defineComponent({
  name: 'GlobalLoader',
  setup() {
    const loaderStore = useLoaderStore();

    const showLoader = computed(() => loaderStore.showLoader);
    const progress = computed(() => loaderStore.progress);
    const loadingText = computed(() => loaderStore.loadingText);

    const getParticleStyle = (index: number) => {
      const angle = (index * 30) * (Math.PI / 180);
      const radius = 80 + Math.random() * 40;
      const delay = index * 0.1;
      const duration = 2 + Math.random() * 2;
      
      return {
        '--delay': `${delay}s`,
        '--duration': `${duration}s`,
        '--x': `${Math.cos(angle) * radius}px`,
        '--y': `${Math.sin(angle) * radius}px`,
      };
    };

    return {
      showLoader,
      progress,
      loadingText,
      getParticleStyle,
    };
  },
});
</script>

<style scoped>
/* Transition animations */
.loader-fade-enter-active,
.loader-fade-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.loader-fade-enter-from,
.loader-fade-leave-to {
  opacity: 0;
}

.loader-fade-enter-from .loader-container,
.loader-fade-leave-to .loader-container {
  transform: scale(0.9);
  opacity: 0;
}

/* Main overlay */
.global-loader-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

/* Backdrop with gradient */
.loader-backdrop {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(12, 129, 136, 0.95) 0%,
    rgba(10, 107, 114, 0.98) 50%,
    rgba(8, 85, 92, 0.95) 100%
  );
  backdrop-filter: blur(10px);
}

/* Main container */
.loader-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  padding: 40px;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Spinner wrapper */
.loader-spinner-wrapper {
  position: relative;
  width: 140px;
  height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Spinner rings */
.spinner-ring {
  position: absolute;
  border-radius: 50%;
  border: 3px solid transparent;
}

.outer-ring {
  width: 140px;
  height: 140px;
  border-top-color: rgba(255, 255, 255, 0.9);
  border-right-color: rgba(255, 255, 255, 0.3);
  animation: spin 1.5s linear infinite;
}

.middle-ring {
  width: 110px;
  height: 110px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  animation: pulse 2s ease-in-out infinite;
}

.inner-ring {
  width: 80px;
  height: 80px;
  border-bottom-color: rgba(255, 255, 255, 0.9);
  border-left-color: rgba(255, 255, 255, 0.3);
  animation: spin 1s linear infinite reverse;
}

/* Center icon */
.spinner-center {
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(5px);
  animation: centerPulse 2s ease-in-out infinite;
}

.center-icon {
  width: 28px;
  height: 28px;
  color: white;
  animation: iconFloat 3s ease-in-out infinite;
}

/* Progress container */
.loader-progress-container {
  display: flex;
  align-items: center;
  gap: 15px;
  width: 280px;
}

.loader-progress-track {
  flex: 1;
  height: 6px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  overflow: hidden;
  position: relative;
}

.loader-progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #fff, rgba(255, 255, 255, 0.8));
  border-radius: 10px;
  transition: width 0.3s ease;
  position: relative;
  overflow: hidden;
}

.progress-glow {
  position: absolute;
  top: 0;
  right: 0;
  width: 30px;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8));
  animation: progressGlow 1.5s ease-in-out infinite;
}

.progress-text {
  font-size: 14px;
  font-weight: 600;
  color: white;
  min-width: 45px;
  text-align: right;
  font-family: 'Segoe UI', system-ui, sans-serif;
}

/* Loading text */
.loader-text-container {
  display: flex;
  align-items: center;
  gap: 4px;
}

.loader-text {
  font-size: 16px;
  font-weight: 500;
  color: white;
  letter-spacing: 0.5px;
  font-family: 'Segoe UI', system-ui, sans-serif;
}

.loader-dots {
  display: flex;
  gap: 3px;
}

.dot {
  width: 6px;
  height: 6px;
  background: white;
  border-radius: 50%;
  animation: dotBounce 1.4s ease-in-out infinite;
}

.dot:nth-child(1) { animation-delay: 0s; }
.dot:nth-child(2) { animation-delay: 0.2s; }
.dot:nth-child(3) { animation-delay: 0.4s; }

/* Floating particles */
.particles {
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.particle {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 8px;
  height: 8px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  animation: particleFloat var(--duration) ease-in-out infinite;
  animation-delay: var(--delay);
  transform: translate(-50%, -50%);
}

/* Keyframe animations */
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes pulse {
  0%, 100% { 
    transform: scale(1);
    opacity: 0.3;
  }
  50% { 
    transform: scale(1.05);
    opacity: 0.6;
  }
}

@keyframes centerPulse {
  0%, 100% { 
    transform: scale(1);
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
  }
  50% { 
    transform: scale(1.1);
    box-shadow: 0 0 40px rgba(255, 255, 255, 0.5);
  }
}

@keyframes iconFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
}

@keyframes dotBounce {
  0%, 80%, 100% { 
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% { 
    transform: scale(1.2);
    opacity: 1;
  }
}

@keyframes progressGlow {
  0%, 100% { 
    opacity: 0;
    transform: translateX(-100%);
  }
  50% { 
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes particleFloat {
  0%, 100% {
    transform: translate(-50%, -50%) translate(0, 0);
    opacity: 0;
  }
  20% {
    opacity: 0.8;
  }
  80% {
    opacity: 0.8;
  }
  100% {
    transform: translate(-50%, -50%) translate(var(--x), var(--y));
    opacity: 0;
  }
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .loader-container {
    padding: 20px;
    gap: 20px;
  }
  
  .loader-spinner-wrapper {
    width: 100px;
    height: 100px;
  }
  
  .outer-ring { width: 100px; height: 100px; }
  .middle-ring { width: 80px; height: 80px; }
  .inner-ring { width: 60px; height: 60px; }
  .spinner-center { width: 40px; height: 40px; }
  .center-icon { width: 22px; height: 22px; }
  
  .loader-progress-container { width: 220px; }
  .loader-text { font-size: 14px; }
}

/* RTL Support */
[dir="rtl"] .loader-progress-container {
  flex-direction: row-reverse;
}

[dir="rtl"] .progress-text {
  text-align: left;
}
</style>

